import { EmojiTransportation } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { ICustomField, Log, StationAdditionFormFields } from "@ryde/shared";

import { NumberInputByControl } from "../../../controls";
import { SwitchTemplate } from "../switch.template";
import { StationAdditionItem } from "./station-addition.item";
import { StationAdditionItemAdd } from "./station-addition.item.add";
import { emptyStationAddition } from "./station-addition.models";
import { StationAdditionsTemplateWrapperStyled } from "./station-addition.styled";
import { normalizeStationAdditionFieldData } from "./station-addition.utils";

export const StationAdditions: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const defaultPriceFieldName = `${name}.${StationAdditionFormFields.defaultPrice}`;
  const priceListFieldName = `${name}.${StationAdditionFormFields.prices}`;
  const excludeEmptyStationsFieldName = `${name}.${StationAdditionFormFields.excludeEmptyStations}`;
  const counterIsPerAreaFieldName = `${name}.${StationAdditionFormFields.counterIsPerArea}`;

  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext();

  const pricingListWatched = useWatch({ control, name: priceListFieldName });

  const pricingList = useMemo(() => {
    const availabilityListValue = normalizeStationAdditionFieldData(
      pricingListWatched,
      name,
      setValue,
    );
    Log.debug("Area addition list normalized value", availabilityListValue);
    return availabilityListValue.toSorted(
      (a, b) => Number(a.price) - Number(b.price),
    );
  }, [name, setValue, pricingListWatched]);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove area addition handler fired for index ", index);

    if (!pricingList?.at(index)) {
      Log.warnAndToast(
        t("errors.ITEM_DONT_EXISTS_IN_LIST"),
        index,
        pricingList,
      );
      return;
    }

    const pricingListUpdated = pricingList.toSpliced(index, 1);
    setValue(priceListFieldName, pricingListUpdated || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  return (
    <StationAdditionsTemplateWrapperStyled>
      <Chip
        label={t("priceRate.formLabel.additionPerEachExtraStation")}
        icon={<EmojiTransportation />}
      />
      <div className="defaultStation__title">
        {t("priceRate.formLabel.priceForEveryExtraStationInRoute")}
        <div className="defaultStation__description">
          ({t("priceRate.formLabel.allExceptFirstAndLast")})
        </div>
      </div>
      <NumberInputByControl
        name={defaultPriceFieldName}
        formLabel={t("priceRate.formLabel.stationsPricing.defaultPrice")}
        formAttributes={{ numberInput: { minValue: 0 } }}
        control={control}
      />
      <div className="additionalSettings__title">
        {t("priceRate.formLabel.additionalSettings")}
      </div>
      {!!pricingList?.length &&
        pricingList.map((priceItem, index) => {
          const uniqueKeyValue = `${priceItem.areaIds?.join("-") || "_"}_${priceItem?.price || 0}`;
          return (
            <StationAdditionItem
              key={uniqueKeyValue}
              fieldName={priceListFieldName}
              control={control}
              index={index}
              setValue={setValue}
              getValues={getValues}
              value={priceItem || emptyStationAddition}
              removeHandler={removeHandler}
            />
          );
        })}
      <StationAdditionItemAdd
        fieldName={priceListFieldName}
        index={pricingList?.length || 0}
        setValue={setValue}
        getValues={getValues}
      />
      <SwitchTemplate name={excludeEmptyStationsFieldName} />
      <SwitchTemplate name={counterIsPerAreaFieldName} />
    </StationAdditionsTemplateWrapperStyled>
  );
};
